/* eslint-disable complexity */
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import {
  AUTHENTICATION_REQUIRED,
  COMPLETED,
  MFA_COMPLETED,
  SHOULD_REMEMBER_USER
} from '../../../constants';
import {
  AuthAccessTokenDetails,
  ContinueAuthentication,
  FlyDeltaPoll,
  ILoginUserNamePasswordInput,
  LoginAuthWithCredentialsResponse,
  LoginOAuthParams,
  SwapAuthForAccessTokens
} from '../../../models';
import {
  isCompletedStatus,
  isRememberDeviceDetailsStatus,
  returnAuthorizationResponseCode
} from '../login-remember-me-utils-service/initiate-login-utils';
import { LoginSubmitAuthenticationIdentifierHelperService } from './login-submit-authentication-identifier-helper.service';

@Injectable({
  providedIn: 'root'
})
export class LoginSubmitAuthenticationIdentifierService extends LoginSubmitAuthenticationIdentifierHelperService {
  submitAuthenticationIdentifier(
    loginOAuthParams: LoginOAuthParams,
    requestInput: ILoginUserNamePasswordInput,
    flowIdValue: string
  ): Observable<AuthAccessTokenDetails> {
    if (flowIdValue === '') {
      flowIdValue = this.flowId;
    }
    return this.loginUserIdentifier
      .loginUserIdentifier(
        loginOAuthParams.oauthConfig,
        flowIdValue,
        requestInput.userName
      )
      .pipe(
        concatMap((response: LoginAuthWithCredentialsResponse) => {
          if (response.status === AUTHENTICATION_REQUIRED) {
            return this.initiateFlyDeltaPoll(loginOAuthParams, flowIdValue);
          } else {
            return throwError(response);
          }
        }),
        concatMap((response: FlyDeltaPoll) => {
          if (response.status === MFA_COMPLETED) {
            return this.continueAuthentication.continueAuthentication(
              loginOAuthParams.oauthConfig,
              response?.id ? response.id : ''
            );
          } else {
            return this.handleFlyDeltaPollResponse(response);
          }
        }),
        concatMap((response: ContinueAuthentication | string) => {
          if (typeof response === 'string') {
            return of(response);
          } else if (response.status === COMPLETED) {
            return this.loginRememberMeUtils.swapAuthCodeForToken(
              loginOAuthParams,
              returnAuthorizationResponseCode(response)
            );
          } else if (response.status === SHOULD_REMEMBER_USER) {
            return this.loginPostWithRememberUser.loginPostWithRememberUser(
              loginOAuthParams.oauthConfig,
              response?.id ? response.id : '',
              requestInput
            );
          } else {
            return throwError(response);
          }
        }),
        concatMap(
          (
            response:
              | SwapAuthForAccessTokens
              | LoginAuthWithCredentialsResponse
              | string
          ) => {
            if (typeof response === 'string') {
              return of(response);
            } else if (
              typeof response === 'object' &&
              'status' in response &&
              isRememberDeviceDetailsStatus(response, loginOAuthParams)
            ) {
              this.assignRememberMeDetails(response);
              return this.loginPostWithContinue.loginPostWithContinue(
                loginOAuthParams.oauthConfig,
                response.id ? response.id : ''
              );
            } else {
              return this.processTokenResponse(response, loginOAuthParams);
            }
          }
        ),
        concatMap(
          (
            response:
              | SwapAuthForAccessTokens
              | LoginAuthWithCredentialsResponse
              | string
          ) => this.processTokenResponse(response, loginOAuthParams)
        )
      );
  }

  processTokenResponse(
    response:
      | SwapAuthForAccessTokens
      | LoginAuthWithCredentialsResponse
      | string,
    loginOAuthParams: LoginOAuthParams
  ): Observable<AuthAccessTokenDetails> {
    if (typeof response === 'string') {
      return of({} as AuthAccessTokenDetails);
    } else if ('access_token' in response) {
      return of(this.createAuthTokenDetails(response));
    } else if (isCompletedStatus(response, loginOAuthParams)) {
      return this.loginRememberMeUtils
        .swapAuthCodeForToken(
          loginOAuthParams,
          returnAuthorizationResponseCode(response)
        )
        .pipe(
          concatMap((swapResponse: SwapAuthForAccessTokens) =>
            of(this.createAuthTokenDetails(swapResponse))
          )
        );
    } else {
      return throwError(response);
    }
  }
}
